import { useState, useEffect, useRef } from "react";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type DealerPortalCustomerJourneyStatus,
  type DealerPortalJourneyPriority,
  type DealerPortalColleague,
} from "@/types/dealerPortal";
import {
  Users,
  Calendar,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  User,
  Car,
  MessageSquare,
  AtSign,
  Loader2,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { getLanguage } from "@/utils/languageUtils";
import {
  dealerPortalLabels,
  getJourneyStatusLabels,
  getJourneyPriorityLabels,
} from "@/i18n";
import { useDealerPortalNativeSheetProps } from "@/lib/native/useDealerPortalNativeSheetProps";

const getDateLocale = () => (getLanguage() === "en" ? "en-GB" : "sv-SE");
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_ORDER: DealerPortalCustomerJourneyStatus[] = [
  "FIRST_CONTACT",
  "PRODUCT_VIEWING",
  "NEGOTIATION",
  "CLOSED",
  "NOT_COMPLETED",
];
const STATUS_COLORS: Record<DealerPortalCustomerJourneyStatus, string> = {
  FIRST_CONTACT: "from-blue-500 to-purple-600",
  PRODUCT_VIEWING: "from-purple-500 to-purple-700",
  NEGOTIATION: "from-pink-500 to-purple-600",
  CLOSED: "from-emerald-500 to-green-600",
  NOT_COMPLETED: "from-orange-500 to-red-600",
};
const STATUS_ICONS: Record<DealerPortalCustomerJourneyStatus, LucideIcon> = {
  FIRST_CONTACT: Users,
  PRODUCT_VIEWING: Calendar,
  NEGOTIATION: TrendingUp,
  CLOSED: CheckCircle,
  NOT_COMPLETED: AlertCircle,
};
const PRIORITY_BADGE: Record<string, string> = {
  HIGH: "bg-red-500/90 text-white",
  MEDIUM: "bg-amber-500/90 text-white",
  LOW: "bg-emerald-500/90 text-white",
};
const PRIORITY_BADGE_HIGHLIGHT: Record<string, string> = {
  HIGH: "data-[highlighted]:!bg-red-500/90",
  MEDIUM: "data-[highlighted]:!bg-amber-500/90",
  LOW: "data-[highlighted]:!bg-emerald-500/90",
};

function formatDate(s: string) {
  return new Date(s).toLocaleString(getDateLocale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Visar händelsetext med översatta etiketter. */
function formatEventDescription(
  description: string | null | undefined,
  eventType: string,
  statusLabels: Record<DealerPortalCustomerJourneyStatus, string>,
  eventLabels: Record<string, string>,
): string {
  const raw = description ?? eventLabels[eventType] ?? eventType;
  let text = raw;
  (Object.keys(statusLabels) as DealerPortalCustomerJourneyStatus[]).forEach(
    (code) => {
      text = text.split(code).join(statusLabels[code]);
    },
  );
  return text;
}

interface Props {
  journeyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function colleagueDisplayName(col: DealerPortalColleague): string {
  if (col.user) {
    return (
      [col.user.firstName, col.user.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || col.id
    );
  }
  return col.id;
}

export function DealerPortalJourneyCardModal({
  journeyId,
  open,
  onClose,
  onSuccess,
}: Props) {
  const [comment, setComment] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [status, setStatus] = useState<DealerPortalCustomerJourneyStatus | "">(
    "",
  );
  const [priority, setPriority] = useState<DealerPortalJourneyPriority | "">(
    "",
  );
  const [assignedActorId, setAssignedActorId] = useState<string>("__none__");
  const [pendingComments, setPendingComments] = useState<
    Array<{ content: string; mentions: string[] }>
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const nativeSheetProps = useDealerPortalNativeSheetProps(
    open,
    "max-w-lg max-h-[68vh] sm:max-h-[90vh] overflow-y-auto",
  );

  const { language } = useLanguage();
  const statusLabels = getJourneyStatusLabels(language);
  const priorityLabels = getJourneyPriorityLabels(language);
  const jm = dealerPortalLabels(language).journeyModal as Record<
    string,
    string
  >;
  const eventLabels: Record<string, string> = {
    STATUS_CHANGE: jm.eventStatusChanged,
    ASSIGNMENT: jm.eventAssignmentUpdated,
    PRIORITY_CHANGE: jm.eventPriorityChanged,
    UPDATED: jm.eventUpdated,
  };
  const { user: currentUser } = useAuth();
  const {
    journey,
    colleagues,
    updateJourney: updateMutation,
    addJourneyComment: commentMutation,
    deleteJourney: deleteJourneyMutation,
  } = useDealerPortal({ journeyId: open && journeyId ? journeyId : null });
  const journeyData = journey.data;
  const isLoading = journey.isLoading;
  const colleaguesData = colleagues.data ?? [];

  const currentStatus = journeyData?.status ?? "";
  const currentAssignedActorId = journeyData?.assignedActorId ?? "__none__";

  useEffect(() => {
    if (journeyData?.status) setStatus(journeyData.status);
  }, [journeyData?.status]);
  useEffect(() => {
    if (journeyData?.priority != null) setPriority(journeyData.priority);
    else setPriority("MEDIUM");
  }, [journeyData?.priority]);
  useEffect(() => {
    setAssignedActorId(journeyData?.assignedActorId ?? "__none__");
  }, [journeyData?.assignedActorId]);

  // Reset pending state when modal closes or journey changes so we don't carry over to another journey
  useEffect(() => {
    if (!open) {
      setPendingComments([]);
    }
  }, [open]);
  useEffect(() => {
    setPendingComments([]);
  }, [journeyId]);

  const displayName =
    journeyData?.customer?.name?.trim() ||
    journeyData?.customer?.email ||
    (dealerPortalLabels(language).journey as Record<string, string>)
      .unknownCustomer;
  const regNr = journeyData?.vehicle?.regNr;

  const currentPriority = journeyData?.priority ?? "MEDIUM";

  const handleStatusChange = (v: string) => {
    setStatus(v as DealerPortalCustomerJourneyStatus);
  };

  const handlePriorityChange = (v: string) => {
    setPriority(v as DealerPortalJourneyPriority);
  };

  const hasChanges =
    (status && status !== currentStatus) ||
    (priority && priority !== currentPriority) ||
    assignedActorId !== currentAssignedActorId ||
    pendingComments.length > 0;

  const handleSaveChanges = async () => {
    if (!journeyId || !hasChanges) return;
    setIsSaving(true);
    try {
      const journeyPayload =
        status !== currentStatus ||
        priority !== currentPriority ||
        assignedActorId !== currentAssignedActorId;
      if (journeyPayload) {
        await updateMutation.mutateAsync({
          id: journeyId,
          body: {
            status: status || currentStatus,
            priority: priority || currentPriority,
            assignedActorId:
              assignedActorId === "__none__" ? null : assignedActorId,
          },
        });
      }
      for (const p of pendingComments) {
        await commentMutation.mutateAsync({
          id: journeyId,
          content: p.content,
          mentions: p.mentions?.length ? p.mentions : [],
        });
      }
      setPendingComments([]);
      onSuccess();
      toast.success(jm.toastSaved ?? "Ändringar sparade");
    } catch (err: unknown) {
      toast.error(
        err instanceof Error
          ? err.message
          : (jm.toastSaveError ?? "Kunde inte spara ändringar"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPendingComments((prev) => [
      ...prev,
      {
        content: comment.trim(),
        mentions: mentions.length > 0 ? mentions : [],
      },
    ]);
    setComment("");
    setMentions([]);
    setShowMentionPopup(false);
    toast.success(jm.toastNoteAdded ?? "Anteckning tillagd");
  };

  const handleCommentKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === "@") setShowMentionPopup(true);
  };

  const insertMention = (col: DealerPortalColleague) => {
    const name = colleagueDisplayName(col);
    setComment((prev) => {
      const withoutTrailingAt = prev.replace(/@\s*$/, "");
      return `${withoutTrailingAt}@${name} `;
    });
    setMentions((prev) => (prev.includes(col.id) ? prev : [...prev, col.id]));
    setShowMentionPopup(false);
  };

  const actorName = (
    a:
      | { user?: { firstName?: string; lastName?: string; username?: string } }
      | null
      | undefined,
  ) => {
    if (!a?.user) return "—";
    const firstLast = [a.user.firstName, a.user.lastName]
      .filter(Boolean)
      .join(" ");
    return firstLast || (a.user as { username?: string }).username || "—";
  };

  const createdEvent = journeyData?.events?.find(
    (e) => e.eventType === "CREATED",
  );
  const otherEvents = (journeyData?.events ?? []).filter(
    (e) => e.eventType !== "CREATED",
  );
  const vehicleLabel = journeyData?.vehicle
    ? `${journeyData.vehicle.regNr} – ${journeyData.vehicle.make} ${journeyData.vehicle.model} (${journeyData.vehicle.year})`
    : null;

  const timelineItems: Array<{
    key: string;
    date: string;
    text: string;
    by?: string;
    isComment?: boolean;
  }> = [];
  if (journeyData?.createdAt) {
    const createdText = vehicleLabel
      ? `${jm.journeyCreated} – ${vehicleLabel}`
      : jm.journeyCreated;
    timelineItems.push({
      key: "created",
      date: journeyData.createdAt,
      text: createdText,
      by: createdEvent ? actorName(createdEvent.actor) : undefined,
    });
  }
  // First note: creation comment (followUpNote) – always first in the notes list
  if (journeyData?.followUpNote?.trim()) {
    timelineItems.push({
      key: "followUpNote",
      date: journeyData.createdAt,
      text: journeyData.followUpNote.trim(),
      by: createdEvent ? actorName(createdEvent.actor) : undefined,
      isComment: true,
    });
  }
  otherEvents.forEach((e) => {
    timelineItems.push({
      key: e.id,
      date: e.createdAt,
      text: formatEventDescription(
        e.description,
        e.eventType,
        statusLabels,
        eventLabels,
      ),
      by: actorName(e.actor),
    });
  });
  (journeyData?.comments ?? []).forEach((c) => {
    timelineItems.push({
      key: `comment-${c.id}`,
      date: c.createdAt,
      text: c.content,
      by: actorName(c.actor),
      isComment: true,
    });
  });
  pendingComments.forEach((p, idx) => {
    timelineItems.push({
      key: `pending-comment-${idx}`,
      date: new Date().toISOString(),
      text: p.content + " " + jm.pendingSave,
      isComment: true,
    });
  });
  timelineItems.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent {...nativeSheetProps}>
          <DialogHeader className="flex flex-row items-start justify-between gap-4">
            <div className="min-w-0 space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <User className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-muted-foreground" />
                </div>
                <DialogTitle className="pr-8 text-sm sm:text-base">
                  {displayName}
                </DialogTitle>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                  <Car className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                </div>
                {regNr ? (
                  <LicensePlate lpn={regNr} className="text-xs sm:text-sm" />
                ) : (
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {jm.noVehicle ?? "Inget fordon"}
                  </p>
                )}
              </div>
            </div>
            {journeyData && (
              <div className="flex flex-col gap-2 shrink-0 min-w-0">
                {colleaguesData.length > 0 && (
                  <div className="flex items-center gap-0">
                    <Label className="text-xs text-muted-foreground shrink-0 w-20 pr-1">
                      {jm.assignment ?? "Tilldelning"}
                    </Label>
                    <Select
                      value={assignedActorId}
                      onValueChange={setAssignedActorId}
                    >
                      <SelectTrigger className="h-7 sm:h-8 text-xs px-2 py-1 min-w-0 flex-1 max-w-[200px] whitespace-nowrap">
                        <SelectValue placeholder={jm.select ?? "Välj"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          {jm.none ?? "Ingen"}
                        </SelectItem>
                        {colleaguesData.map((col) => {
                          const name = col.user
                            ? `${col.user.firstName} ${col.user.lastName}`.trim() ||
                              col.user.email
                            : col.id;
                          const isCurrentUser =
                            currentUser?.id &&
                            col.user?.id &&
                            String(col.user.id) === String(currentUser.id);
                          return (
                            <SelectItem key={col.id} value={col.id}>
                              <span className="flex items-center gap-2">
                                {name}
                                {isCurrentUser && (
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px] px-1.5 py-0 font-normal"
                                  >
                                    {jm.you ?? "Du"}
                                  </Badge>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-0">
                  <Label className="text-xs text-muted-foreground shrink-0 w-20 pr-1">
                    {jm.status ?? "Status"}
                  </Label>
                  <Select
                    value={status || journeyData.status}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger
                      className={
                        status || journeyData?.status
                          ? `h-7 sm:h-8 text-xs px-2 py-1 min-w-0 flex-1 max-w-[140px] bg-gradient-to-r ${STATUS_COLORS[(status || journeyData?.status) as DealerPortalCustomerJourneyStatus]} text-white border-0 [&>svg]:text-white [&>svg]:h-3 sm:[&>svg]:h-3.5 [&>svg]:w-3 sm:[&>svg]:w-3.5`
                          : "h-7 sm:h-8 text-xs px-2 py-1 min-w-0 flex-1 max-w-[140px]"
                      }
                    >
                      <SelectValue>
                        {status || journeyData?.status ? (
                          <span className="flex items-center gap-1 sm:gap-1.5">
                            {(() => {
                              const Icon =
                                STATUS_ICONS[
                                  (status ||
                                    journeyData?.status) as DealerPortalCustomerJourneyStatus
                                ];
                              return (
                                <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 opacity-90" />
                              );
                            })()}
                            {
                              statusLabels[
                                (status ||
                                  journeyData?.status) as DealerPortalCustomerJourneyStatus
                              ]
                            }
                          </span>
                        ) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_ORDER.map((s) => {
                        const Icon = STATUS_ICONS[s];
                        return (
                          <SelectItem
                            key={s}
                            value={s}
                            className={`bg-gradient-to-r ${STATUS_COLORS[s]} text-white border-0 group focus:text-white data-[highlighted]:text-white`}
                          >
                            <span className="inline-flex items-center gap-2 transition-transform duration-200 group-data-[highlighted]:scale-[1.05]">
                              <Icon className="h-4 w-4 shrink-0 opacity-90" />
                              {statusLabels[s]}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-0">
                  <Label className="text-xs text-muted-foreground shrink-0 w-20 pr-1">
                    {jm.priority ?? "Prioritet"}
                  </Label>
                  <Select
                    value={priority || currentPriority}
                    onValueChange={handlePriorityChange}
                  >
                    <SelectTrigger
                      className={`h-7 sm:h-8 text-xs px-2 py-1 min-w-0 flex-1 max-w-[100px] rounded-full ${PRIORITY_BADGE[priority || currentPriority] ?? PRIORITY_BADGE.MEDIUM} border-0 text-white hover:brightness-95 [&>svg]:text-white [&>svg]:opacity-90 [&>svg]:h-3 sm:[&>svg]:h-3.5 [&>svg]:w-3 sm:[&>svg]:w-3.5`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(
                        Object.keys(
                          priorityLabels,
                        ) as DealerPortalJourneyPriority[]
                      ).map((p) => (
                        <SelectItem
                          key={p}
                          value={p}
                          className={`${PRIORITY_BADGE[p] ?? PRIORITY_BADGE.MEDIUM} ${PRIORITY_BADGE_HIGHLIGHT[p] ?? PRIORITY_BADGE_HIGHLIGHT.MEDIUM} text-white rounded-sm border-0 group focus:text-white data-[highlighted]:text-white`}
                        >
                          <span className="inline-block transition-transform duration-200 group-data-[highlighted]:scale-[1.05]">
                            {priorityLabels[p]}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </DialogHeader>
          {isLoading ? (
            <p className="text-muted-foreground text-xs sm:text-sm">
              {dealerPortalLabels(language).loading ?? "Laddar..."}
            </p>
          ) : !journeyData ? (
            <p className="text-muted-foreground text-xs sm:text-sm">
              {jm.toastLoadError ?? "Kunde inte ladda kundresa."}
            </p>
          ) : (
            <div className="space-y-6">
              <div>
                <h4 className="text-xs sm:text-sm font-medium my-2">
                  {jm.eventsAndNotes ?? "Händelser och anteckningar"}
                </h4>
                <div className="space-y-1.5 rounded-md border bg-muted/20 p-1.5">
                  {timelineItems.length === 0 ? (
                    <p className="text-xs sm:text-sm text-muted-foreground py-1.5">
                      {jm.noEvents ?? "Inga händelser än."}
                    </p>
                  ) : (
                    timelineItems.map((item) => (
                      <div
                        key={item.key}
                        className={`text-xs sm:text-sm rounded border px-2 py-1.5 flex items-center gap-2 ${
                          item.isComment
                            ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200/60 dark:border-blue-800/60"
                            : "bg-background border-border/60"
                        }`}
                      >
                        {item.isComment && (
                          <MessageSquare className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground leading-snug whitespace-pre-line">
                            {item.text}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {item.by && item.by !== "—" ? (
                              <>
                                <span>
                                  {jm.by ?? "av"} {item.by}
                                </span>
                                <span
                                  className="mx-1.5 text-muted-foreground/70"
                                  aria-hidden
                                >
                                  ·
                                </span>
                                <span>{formatDate(item.date)}</span>
                              </>
                            ) : (
                              formatDate(item.date)
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <form
                onSubmit={handleAddComment}
                className="space-y-2 pt-4 border-t"
              >
                <Label>{jm.writeNote ?? "Skriv en anteckning"}</Label>
                <p className="text-xs text-muted-foreground">
                  {jm.mentionHint ??
                    "Använd @ för att nämna en kollega – de får notis och e-post."}
                </p>
                <div className="relative">
                  <textarea
                    ref={commentTextareaRef}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder={jm.placeholder ?? "Skriv en anteckning..."}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={handleCommentKeyDown}
                  />
                  {showMentionPopup && (
                    <div className="absolute bottom-full left-0 mt-1 w-64 max-h-48 overflow-y-auto rounded-md border bg-card p-2 shadow-lg z-10">
                      {colleaguesData.length === 0 ? (
                        <p className="px-2 py-1 text-sm text-muted-foreground">
                          {jm.noColleagues ?? "Inga kollegor tillgängliga"}
                        </p>
                      ) : (
                        colleaguesData.map((col) => (
                          <button
                            key={col.id}
                            type="button"
                            onClick={() => insertMention(col)}
                            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                          >
                            <AtSign className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            {colleagueDisplayName(col)}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMentionPopup((v) => !v)}
                    className="shrink-0"
                  >
                    <AtSign className="h-3.5 w-3.5" />
                    {jm.mentionColleague ?? "Nämn kollega"}
                  </Button>
                  {mentions.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {mentions.length} {jm.mentioned ?? "nämnd(e)"}
                    </span>
                  )}
                  <Button type="submit" size="sm" disabled={!comment.trim()}>
                    {jm.addNote ?? "Lägg till"}
                  </Button>
                </div>
              </form>

              <div className="flex justify-end items-center gap-2 pt-4 border-t">
                <Button
                  type="button"
                  size="sm"
                  disabled={!hasChanges || isSaving}
                  onClick={handleSaveChanges}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                      {jm.deleting ?? "Sparar..."}
                    </>
                  ) : (
                    (jm.saveChanges ?? "Spara ändringar")
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  disabled={isDeleting}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  {isDeleting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 shrink-0" />
                  )}
                  {jm.deleteButton ?? "Radera"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {jm.deleteTitle ?? "Radera kundresa?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {jm.deleteDesc ??
                "Är du säker på att du vill radera denna kundresa? Alla händelser och anteckningar tas bort permanent. Detta går inte att ångra."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {(
                dealerPortalLabels(language).customers as Record<string, string>
              ).cancel ?? "Avbryt"}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={async (e) => {
                e.preventDefault();
                if (!journeyId) return;
                setIsDeleting(true);
                try {
                  await deleteJourneyMutation.mutateAsync(journeyId);
                  setDeleteConfirmOpen(false);
                  onClose();
                  onSuccess();
                  toast.success(jm.toastDeleted ?? "Kundresa raderad");
                } catch (err: unknown) {
                  toast.error(
                    err instanceof Error
                      ? err.message
                      : (jm.toastDeleteError ?? "Kunde inte radera kundresa"),
                  );
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                  {jm.deletingLabel ?? "Raderar..."}
                </>
              ) : (
                (jm.deleteButton ?? "Radera")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
